import * as React from 'react'

import { isSameDay, startOfMonth } from 'date-fns'
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  ErrorText,
  Flex,
  Grid,
  Label,
  RadioButton,
  Select,
  SelectOptionInterface,
  Separator,
  Stack,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'
import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  DealPosition,
  DeliveryHourType,
  DeliveryPeriodType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import {
  DealPositionBadge,
  EEXContractModel,
  FormNumberInputWithUnit,
  convertAreaLabelFromAreaCode,
  convertDeliveryHourTypeLabel,
  convertDeliveryPeriodTypeLabel,
} from '@enechain/eposition-ui'
import { DeliveryDateRangePicker } from '@enechain/eposition-ui/src/components/features/TransactionForm/DeliveryDateRangePicker'
import {
  formatYYYYMM,
  getEndOfMonthDay,
  stringUtils,
} from '@enechain/eposition-utils'

import { useTransactionFormOption } from '~/pages/Simulation/hooks/useTransactionFormOption'

import { useEditableSimulationTransaction } from './EditableTransactionProvider'

type FloatingIndexOption = {
  label: string
  value: string
}

type Props<T extends FieldValues> = {
  schemaKeyName: FieldPath<T>
  control: Control<T>
  deliveryType?: string
  unitPriceLabel?: string
  dealPosition: DealPosition
  isPhysicalFuture: boolean
  resetLegs?: () => void
  floatingIndexOptions?: FloatingIndexOption[]
}

export const SummaryTransactionForm = <T extends FieldValues>({
  schemaKeyName,
  control,
  deliveryType,
  unitPriceLabel,
  dealPosition,
  isPhysicalFuture,
  resetLegs,
  floatingIndexOptions,
}: Props<T>): React.ReactElement => {
  const { t } = useTranslation(['domain', 'common'])

  const contextEditableColumns = useEditableSimulationTransaction()
  const editableColumns = {
    areaCode: true,
    deliveryPeriodType: true,
    deliveryDateRange: true,
    deliveryHourType: true,
    unitPricePerKwh: true,
    volumeInKw: true,
    ...contextEditableColumns,
  }

  const {
    field: areaCodeField,
    fieldState: { error: areaCodeError },
  } = useController({
    name: `${schemaKeyName}.areaCode` as FieldPath<T>,
    control,
  })

  const { field: deliveryPeriodTypeField } = useController({
    name: `${schemaKeyName}.deliveryPeriodType` as FieldPath<T>,
    control,
  })

  const {
    field: deliveryDateRangeField,
    fieldState: { error: deliveryDateRangeError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange` as FieldPath<T>,
    control,
  })

  const {
    fieldState: { error: deliveryStartDateError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange.deliveryStartDate` as FieldPath<T>,
    control,
  })

  const {
    fieldState: { error: deliveryEndDateError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange.deliveryEndDate` as FieldPath<T>,
    control,
  })

  const {
    field: deliveryHourTypeField,
    fieldState: { error: deliveryHourTypeError },
  } = useController({
    name: `${schemaKeyName}.deliveryHourType` as FieldPath<T>,
    control,
  })

  const { field: eexProductCodeField } = useController({
    name: `${schemaKeyName}.eexProductCode` as FieldPath<T>,
    control,
  })

  const { field: eexInstrumentField } = useController({
    name: `${schemaKeyName}.eexInstrument` as FieldPath<T>,
    control,
  })

  const { field: unitPricePerKwhField } = useController({
    name: `${schemaKeyName}.unitPricePerKwh` as FieldPath<T>,
    control,
  })

  const { field: volumeInKwField } = useController({
    name: `${schemaKeyName}.volumeInKw` as FieldPath<T>,
    control,
  })

  const { field: floatingIndexCodeField } = useController({
    name: `${schemaKeyName}.floatingIndexCode` as FieldPath<T>,
    control,
  })

  const watchedDeliveryPeriodType = useWatch({
    name: `${schemaKeyName}.deliveryPeriodType` as FieldPath<T>,
    control,
  })

  const { areas, deliveryHourTypes, deliveryPeriodTypes, eexContracts } =
    useTransactionFormOption({
      isSummary: true,
      selectedAreaCode: areaCodeField.value,
      selectedDeliveryPeriodType: deliveryPeriodTypeField.value,
      selectedDeliveryHourType: deliveryHourTypeField.value,
      isPhysicalFuture,
    })

  const shouldSplitDeliveryPeriod =
    watchedDeliveryPeriodType === DeliveryPeriodType.MULTI_MONTH
  const shouldShowDeliveryDateRangePicker =
    isPhysicalFuture === false || shouldSplitDeliveryPeriod

  const deliveryDateRangeErrorMessages =
    deliveryDateRangeError?.message ??
    deliveryStartDateError?.message ??
    deliveryEndDateError?.message

  return (
    <Stack gap="4">
      <Grid
        alignItems="center"
        gridGap="4"
        gridTemplateColumns="1fr 4fr"
        gridTemplateRows="42px 67px 42px auto 42px"
      >
        <Label>{t('domain:shared.position.title')}</Label>
        <DealPositionBadge positionType={dealPosition} />
        <Label required>{t('domain:shared.area.title')}</Label>
        {editableColumns.areaCode ? (
          <Stack gap="1">
            <Select<
              {
                label: string
                value: AreaCode
              } & SelectOptionInterface,
              false
            >
              isMulti={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              getOptionValue={(option) => option.value.toString()}
              onChange={(item): void => {
                if (item?.value === undefined) {
                  areaCodeField.onChange(AreaCode.UNSPECIFIED)
                  return
                }
                areaCodeField.onChange(item.value)
              }}
              options={areas}
              selectedOptions={
                areas.find((area) => area.value === areaCodeField.value) ?? {
                  label: t('common:action.neutral.of_select', {
                    value: t('domain:shared.area.title'),
                  }),
                  value: AreaCode.UNSPECIFIED,
                }
              }
            />
            {areaCodeError?.message !== undefined && (
              <ErrorText>{areaCodeError.message}</ErrorText>
            )}
          </Stack>
        ) : (
          <Text textStyle="textMd">
            {convertAreaLabelFromAreaCode(areaCodeField.value)}
          </Text>
        )}
        <Label required>{t('domain:shared.delivery_period_type.title')}</Label>
        {editableColumns.deliveryPeriodType ? (
          <RadioButton
            onValueChange={({ value }): void => {
              deliveryPeriodTypeField.onChange(Number(value))
              deliveryDateRangeField.onChange({
                deliveryStartDate: undefined,
                deliveryEndDate: undefined,
              })
              if (resetLegs !== undefined) {
                resetLegs()
              }
            }}
            value={deliveryPeriodTypeField.value?.toString() ?? ''}
            radios={deliveryPeriodTypes}
          />
        ) : (
          <Text textStyle="textMd">
            {convertDeliveryPeriodTypeLabel(deliveryPeriodTypeField.value)}
          </Text>
        )}
        <Label required>{t('domain:shared.delivery_hour_type.title')}</Label>
        {editableColumns.deliveryHourType ? (
          <Stack gap="1">
            <Select<
              {
                label: string
                value: DeliveryHourType
              } & SelectOptionInterface,
              false
            >
              isMulti={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              getOptionValue={(option) => option.value.toString()}
              onChange={(item): void => {
                if (item?.value === undefined) {
                  deliveryHourTypeField.onChange(DeliveryHourType.UNSPECIFIED)
                  return
                }
                deliveryHourTypeField.onChange(item.value)
              }}
              options={deliveryHourTypes}
              selectedOptions={
                deliveryHourTypes.find(
                  (item) => item.value === deliveryHourTypeField.value,
                ) ?? {
                  label: t('common:action.neutral.of_select', {
                    value: t('domain:shared.delivery_hour_type.title'),
                  }),
                  value: DeliveryHourType.UNSPECIFIED,
                }
              }
            />
            {deliveryHourTypeError?.message !== undefined && (
              <ErrorText>{deliveryHourTypeError.message}</ErrorText>
            )}
          </Stack>
        ) : (
          <Badge status="neutral" width="fit-content">
            <Text textStyle="textMd">
              {convertDeliveryHourTypeLabel(deliveryHourTypeField.value)}
            </Text>
          </Badge>
        )}
        <Label required>{t('domain:shared.delivery_date_range')}</Label>
        {editableColumns.deliveryDateRange ? (
          shouldShowDeliveryDateRangePicker ? (
            <Stack gap="1">
              <DeliveryDateRangePicker
                schemaKeyName={schemaKeyName}
                control={control}
                deliveryPeriodType={deliveryPeriodTypeField.value}
                handleChange={() => {
                  if (resetLegs !== undefined) {
                    resetLegs()
                  }
                }}
              />
              {deliveryDateRangeErrorMessages !== undefined && (
                <ErrorText>{deliveryDateRangeErrorMessages}</ErrorText>
              )}
            </Stack>
          ) : (
            <Stack gap="1">
              <Select<EEXContractModel & SelectOptionInterface, false>
                isMulti={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                getOptionValue={(option) =>
                  option.value.deliveryStartDate.toString()
                }
                onChange={(item): void => {
                  if (item?.value === undefined) {
                    deliveryDateRangeField.onChange({
                      deliveryStartDate: startOfMonth(new Date()),
                      deliveryEndDate: getEndOfMonthDay(new Date()),
                    })
                    return
                  }
                  deliveryDateRangeField.onChange({
                    deliveryStartDate: item.value.deliveryStartDate,
                    deliveryEndDate: getEndOfMonthDay(
                      item.value.deliveryEndDate,
                    ),
                  })
                  if (resetLegs !== undefined) {
                    resetLegs()
                  }
                  eexProductCodeField.onChange(item.value.code)
                  eexInstrumentField.onChange(item.value.instrument)
                }}
                options={eexContracts}
                selectedOptions={
                  eexContracts.find(
                    (contract: EEXContractModel & SelectOptionInterface) =>
                      isSameDay(
                        contract.value.deliveryStartDate,
                        deliveryDateRangeField.value?.deliveryStartDate ??
                          new Date(),
                      ) &&
                      isSameDay(
                        contract.value.deliveryEndDate,
                        deliveryDateRangeField.value?.deliveryEndDate ??
                          new Date(),
                      ),
                  ) ?? {
                    label: t('common:action.neutral.of_select', {
                      value: t('domain:shared.delivery_date_range'),
                    }),
                    value: {
                      deliveryStartDate: startOfMonth(new Date()),
                      deliveryEndDate: getEndOfMonthDay(new Date()),
                      code: '',
                      instrument: '',
                    },
                  }
                }
              />
              {deliveryDateRangeErrorMessages !== undefined && (
                <ErrorText>{deliveryDateRangeErrorMessages}</ErrorText>
              )}
            </Stack>
          )
        ) : (
          <Text textStyle="textMd">
            {`${deliveryDateRangeField.value?.deliveryStartDate !== undefined ? formatYYYYMM(deliveryDateRangeField.value.deliveryStartDate, '/') : ''} ~ ${deliveryDateRangeField.value?.deliveryEndDate !== undefined ? formatYYYYMM(deliveryDateRangeField.value.deliveryEndDate, '/') : ''}`}
          </Text>
        )}
        {deliveryType !== undefined && (
          <>
            <Label required>
              {t('domain:model.posting.column.trade_kind')}
            </Label>
            <Badge status="neutral" width="fit-content">
              <Text textStyle="textMd">{deliveryType}</Text>
            </Badge>
          </>
        )}
      </Grid>
      <Separator />
      {floatingIndexOptions !== undefined && !isPhysicalFuture && (
        <>
          <Grid
            alignItems="center"
            gridGap="4"
            gridTemplateColumns="1fr 4fr"
            gridTemplateRows="42px"
          >
            <Flex gap="1" alignItems="center">
              <Label>{t('domain:shared.floating_index_formula.title')}</Label>
              <Tooltip
                label={t('domain:shared.floating_index_formula.tooltip')}
                showArrow
              >
                <FaCircleQuestionRegular color="Object.midEmphasis" />
              </Tooltip>
            </Flex>
            <Select<FloatingIndexOption & SelectOptionInterface, false>
              isMulti={false}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              getOptionValue={(option) => option.value}
              onChange={(item): void => {
                floatingIndexCodeField.onChange(item?.value ?? null)
              }}
              options={floatingIndexOptions}
              selectedOptions={
                floatingIndexOptions.find(
                  (option) =>
                    floatingIndexCodeField.value != null &&
                    option.value === floatingIndexCodeField.value,
                ) ?? null
              }
            />
          </Grid>
          <Separator />
        </>
      )}
      <Grid
        alignItems="center"
        gridGap="4"
        gridTemplateColumns="1fr 4fr"
        gridTemplateRows="auto auto"
      >
        <Label required>
          {unitPriceLabel !== undefined
            ? unitPriceLabel
            : t('domain:shared.unit_price.title')}
        </Label>
        {editableColumns.unitPricePerKwh ? (
          <FormNumberInputWithUnit
            unit={t('common:unit.yen_per_kwh')}
            name={`${schemaKeyName}.unitPricePerKwh` as FieldPath<T>}
            control={control}
            placeholder={t('common:form.placeholder.input_entry', {
              value: t('domain:shared.unit_price.title'),
            })}
          />
        ) : (
          <Text textStyle="textMd">{unitPricePerKwhField.value}</Text>
        )}
        <Label required>{t('domain:shared.volume.title')}</Label>
        {editableColumns.volumeInKw ? (
          <FormNumberInputWithUnit
            unit={t('common:unit.mw')}
            name={`${schemaKeyName}.volumeInKw` as FieldPath<T>}
            control={control}
            step={!isPhysicalFuture ? 0.1 : 1}
            placeholder={t('common:form.placeholder.input_entry', {
              value: t('domain:shared.volume.title'),
            })}
          />
        ) : (
          <Text textStyle="textMd">
            {stringUtils.isNotEmpty(volumeInKwField.value)
              ? volumeInKwField.value
              : '0'}
            {t('common:unit.mw')}
          </Text>
        )}
      </Grid>
    </Stack>
  )
}
